// Persistent primary navigation for iPad and desktop.
// Hidden below 1100px so phone and iPad keep the bottom tab bar.
import { Link, useSearch } from 'wouter';
import {
  Apple,
  BarChart3,
  BookHeart,
  BookMarked,
  CalendarDays,
  ClipboardCheck,
  Dumbbell,
  Gauge,
  Home,
  LayoutGrid,
  MessageSquare,
  NotebookPen,
  UserCircle,
  UserRound,
  Users,
} from 'lucide-react';
import { STANDARD_LOGO_SRC } from '@/lib/brandAssets';
import { resolveStandardRoute } from '@/config/standardRoutes';
import { WorkspaceSwitch, type WorkspaceMode } from './WorkspaceSwitch';
import '@/styles/standard-shell.css';

type NavItem = {
  href: string;
  /** Used only when the route registry has no entry for the target. */
  fallbackLabel: string;
  /** Overrides the registry title when the sidebar names the job differently. */
  label?: string;
  icon: typeof Home;
  current: (location: string, view: string | null) => boolean;
  /** Messages row: hidden for self-directed members and carries the unread badge. */
  messages?: boolean;
};

type NavGroup = { label: string; items: NavItem[] };

const startsWithAny = (location: string, paths: string[]) =>
  paths.some((path) => location === path || location.startsWith(`${path}/`));

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Daily',
    items: [
      { href: '/client', fallbackLabel: 'Today', icon: Home, current: (location) => location === '/client' },
      {
        href: '/client/training',
        fallbackLabel: 'Training',
        icon: Dumbbell,
        current: (location) => startsWithAny(location, ['/client/training', '/client/workout', '/client/program', '/client/programs', '/client/history', '/client/exercises']),
      },
      { href: '/client/nutrition', fallbackLabel: 'Nutrition', icon: Apple, current: (location) => startsWithAny(location, ['/client/nutrition']) },
      {
        href: '/client/faith',
        fallbackLabel: 'Faith',
        icon: BookHeart,
        current: (location) => startsWithAny(location, ['/client/faith', '/client/devotional', '/client/bible', '/client/word-study', '/client/divine-mandate', '/client/reading-plans', '/client/coach-playlists']),
      },
      { href: '/client/journal', fallbackLabel: 'Journal', icon: NotebookPen, current: (location) => startsWithAny(location, ['/client/journal']) },
    ],
  },
  {
    label: 'Community',
    items: [
      {
        href: '/client/community',
        fallbackLabel: 'Community',
        icon: Users,
        // Groups is a tab inside Community, so ?view=groups and group pages keep Community current.
        current: (location) => startsWithAny(location, ['/client/community', '/coach/community', '/community/groups']),
      },
      {
        href: '/client/comms',
        fallbackLabel: 'Messages',
        label: 'Messages',
        icon: MessageSquare,
        messages: true,
        current: (location) => startsWithAny(location, ['/client/comms', '/coach/inbox']),
      },
    ],
  },
  {
    label: 'Yours',
    items: [
      { href: '/client/progress', fallbackLabel: 'Progress', icon: BarChart3, current: (location) => startsWithAny(location, ['/client/progress', '/coach/my/progress']) },
      { href: '/client/library/vault', fallbackLabel: 'Vault', icon: BookMarked, current: (location) => startsWithAny(location, ['/client/library/vault']) },
    ],
  },
];

// Command workspace: the coach's own work comes first, then their training.
const COACH_GROUP: NavGroup = {
  label: 'Coach',
  items: [
    { href: '/coach', fallbackLabel: 'Command', label: 'Command', icon: Gauge, current: (location) => location === '/coach' },
    { href: '/coach/clients', fallbackLabel: 'Clients', label: 'Clients', icon: UserRound, current: (location) => startsWithAny(location, ['/coach/clients']) },
    { href: '/coach/inbox', fallbackLabel: 'Inbox', label: 'Inbox', icon: MessageSquare, messages: true, current: (location) => startsWithAny(location, ['/coach/inbox']) },
    { href: '/coach/schedule', fallbackLabel: 'Schedule', label: 'Schedule', icon: CalendarDays, current: (location) => startsWithAny(location, ['/coach/schedule']) },
    { href: '/coach/floor', fallbackLabel: 'Studio floor', label: 'Studio floor', icon: Dumbbell, current: (location) => startsWithAny(location, ['/coach/floor']) },
    { href: '/coach/accountability', fallbackLabel: 'Check-ins', label: 'Check-ins', icon: ClipboardCheck, current: (location) => startsWithAny(location, ['/coach/accountability']) },
  ],
};
const COACH_TRAINING_LABEL: Record<string, string> = { Daily: 'My training', Community: 'Community', Yours: 'Yours' };

function labelFor(item: NavItem) {
  if (item.label) return item.label;
  // Query strings select a view inside a route, so they keep their own label.
  if (item.href.includes('?')) return item.fallbackLabel;
  return resolveStandardRoute(item.href)?.title ?? item.fallbackLabel;
}

export type WidePrimaryNavUser = {
  id: number;
  name?: string | null;
  fullName?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
};

export function WidePrimaryNav({
  location,
  hrefFor,
  user,
  workspaceMode,
  onAllTools,
  onBrandClick,
  showMessages = false,
  unreadMessages = 0,
}: {
  location: string;
  hrefFor: (href: string) => string;
  user?: WidePrimaryNavUser | null;
  /** Coach and admin accounts get the Train and Command switch. */
  workspaceMode?: WorkspaceMode;
  onAllTools: () => void;
  onBrandClick?: () => void;
  /** Mirrors the top bar Messages icon: false for self-directed members. */
  showMessages?: boolean;
  /** Same unread count the top bar Messages icon shows. */
  unreadMessages?: number;
}) {
  const search = useSearch();
  const coachMode = workspaceMode === 'coach';
  // In Command the Inbox row carries messages, so the mirrored Messages row would repeat it.
  const groups = coachMode
    ? [COACH_GROUP, ...NAV_GROUPS.map((group) => ({ label: COACH_TRAINING_LABEL[group.label] ?? group.label, items: group.items.filter((item) => !item.messages) }))]
    : NAV_GROUPS;
  const view = new URLSearchParams(search).get('view');
  const meHref = user ? `/client/members/${user.id}` : '/client/account';
  // Me covers the own profile and its Settings and Membership tabs.
  const meCurrent = (user ? location === meHref : false) || location.startsWith('/client/account') || location.startsWith('/client/billing');
  const displayName = (user?.fullName ?? user?.name ?? user?.email ?? '').trim();
  const initial = (displayName || 'U').slice(0, 1).toUpperCase();

  const brand = (
    <>
      <img src={STANDARD_LOGO_SRC} alt="" />
      <span className="wide-primary-nav__brand-copy">
        <strong>The Standard</strong>
        <span>Live the work.</span>
      </span>
    </>
  );

  return (
    <aside className="wide-primary-nav" aria-label="Main navigation">
      {onBrandClick ? (
        <button type="button" className="wide-primary-nav__brand" onClick={onBrandClick} title="Tap to refresh app" aria-label="The Standard. Refresh app">
          {brand}
        </button>
      ) : (
        <div className="wide-primary-nav__brand">{brand}</div>
      )}

      {workspaceMode ? <WorkspaceSwitch mode={workspaceMode} variant="sidebar" /> : null}

      <nav className="wide-primary-nav__groups" aria-label="Sections">
        {groups.map((group) => (
          <div key={group.label} className="wide-primary-nav__group" role="group" aria-labelledby={`wide-nav-${group.label}`}>
            <p className="wide-primary-nav__label" id={`wide-nav-${group.label}`}>{group.label}</p>
            {group.items.map((item) => {
              if (item.messages && !showMessages) return null;
              const Icon = item.icon;
              // Coach rows are real coach routes; personal rows map onto the coach mirror.
              const href = item.href.startsWith('/coach') ? item.href : hrefFor(item.href);
              const current = item.current(location, view);
              const label = labelFor(item);
              const unread = item.messages && unreadMessages > 0 ? unreadMessages : 0;
              return (
                <Link
                  key={item.href}
                  href={href}
                  className="wide-primary-nav__item"
                  aria-current={current ? 'page' : undefined}
                  aria-label={unread ? `${label}, ${unread} unread` : undefined}
                >
                  <Icon aria-hidden="true" />
                  <span>{label}</span>
                  {unread ? (
                    <span className="wide-primary-nav__badge" aria-hidden="true">{unread > 99 ? '99+' : unread}</span>
                  ) : null}
                </Link>
              );
            })}
            {group.label === 'Yours' ? (
              <button type="button" className="wide-primary-nav__item" onClick={onAllTools} aria-haspopup="dialog">
                <LayoutGrid aria-hidden="true" />
                <span>All tools</span>
              </button>
            ) : null}
          </div>
        ))}
      </nav>

      <Link
        href={hrefFor(meHref)}
        className="wide-primary-nav__me"
        aria-current={meCurrent ? 'page' : undefined}
      >
        <span className="wide-primary-nav__avatar" aria-hidden="true">
          {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : displayName ? initial : <UserCircle />}
        </span>
        <span className="wide-primary-nav__me-copy">
          <strong>{displayName || 'My profile'}</strong>
          <span>Profile and settings</span>
        </span>
      </Link>
    </aside>
  );
}
